/**
=========================================================
* Course Page
* Course page for answering questions
=========================================================
*/

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SbiHeader from "../../components/SbiHeader";
import { useMaterialUIController } from "context";
import { useBranding, FRESENIUS } from "contextBranding";
import colors, { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";

// Course components
import CourseProgressRunner from "./components/CourseProgressRunner";

// Course content components
import { courseConfig as MOTYBConfig, getQuestionComponent as getMOTYBQuestion } from "./questionnaires/motyp";
import NuDESCCourse from "./questionnaires/nudexc";
import { courseConfig as EEGSignalsConfig, getQuestionComponent as getEEGQuestion } from "./questionnaires/eegsignals";
import ExamCourse from "./questionnaires/exam";

function CoursePage() {
  const { coursename } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [controller] = useMaterialUIController();
  const { sbiDashboardTheme } = controller;
  const { isFreseniusBranding } = useBranding();
  const isLight = sbiDashboardTheme === "light";
  const theme = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const contentBg = isFreseniusBranding ? freseniusTheme.bgMain : theme.bgMain;
  const contentColor = isFreseniusBranding ? freseniusTheme.textPrimary : theme.textPrimary;

  const sb = colors.backgroundSbi;
  const cardBg = isFreseniusBranding
    ? (isLight ? freseniusTheme.bgHeader : `${FRESENIUS.blueDark}ee`)
    : (isLight ? theme.bgHeader : `${sb.default}99`);
  const cardBorder = isFreseniusBranding
    ? (isLight ? FRESENIUS.blue : freseniusTheme.border)
    : (isLight ? theme.border : `${sb.default}80`);

  const prim = colors.primarySbi;
  const primaryBtnBg = isFreseniusBranding ? FRESENIUS.blue : prim.main;
  const primaryBtnHoverBg = isFreseniusBranding ? FRESENIUS.blueLight : prim.focus;
  const primaryBtnDisabledBg = isFreseniusBranding ? `${FRESENIUS.blueLight}4D` : `${sb.secondary}4D`;
  const primaryBtnColor = isFreseniusBranding ? FRESENIUS.white : (colors.white?.main || "#fff");

  const secondaryBtnBg = "transparent";
  const secondaryBtnBorder = isFreseniusBranding ? freseniusTheme.border : `${sb.default}80`;
  const secondaryBtnColor = isFreseniusBranding
    ? freseniusTheme.textPrimary
    : (isLight ? theme.textPrimary : (colors.white?.main || "#fff"));
  const secondaryBtnHoverBg = isFreseniusBranding ? `${FRESENIUS.blueLight}22` : `${sb.secondary}33`;

  // Decode course name from URL parameter
  const courseTitle = decodeURIComponent(coursename || "");

  // Reset step when course changes
  useEffect(() => {
    setCurrentStep(1);
  }, [courseTitle]);

  const handleBack = () => {
    navigate("/e-learning");
  };

  // Get course configuration
  const getCourseConfig = () => {
    const configMap = {
      "Month Of The Year Backwards (MOTYB)": MOTYBConfig,
      "NuDESC": { title: "NuDESC", subtitle: "Nu-DESC assessment protocol", totalSteps: 1 },
      "EEG Signals": EEGSignalsConfig,
      "Exam": { title: "Exam", subtitle: "Final assessment", totalSteps: 1 },
    };
    return configMap[courseTitle] || { title: courseTitle, subtitle: "", totalSteps: 1 };
  };

  const courseConfig = getCourseConfig();
  const totalSteps = courseConfig.totalSteps || 1;

  // Get question component for current step
  const getQuestionForStep = () => {
    if (courseTitle === "EEG Signals") {
      return getEEGQuestion(currentStep);
    }
    if (courseTitle === "Month Of The Year Backwards (MOTYB)") {
      return getMOTYBQuestion(currentStep);
    }
    // For other courses, return their component
    const courseMap = {
      "NuDESC": <NuDESCCourse />,
      "Exam": <ExamCourse />,
    };
    return courseMap[courseTitle] || (
      <MDTypography variant="body1" color="white" opacity={0.6}>
        Course content not found for: {courseTitle}
      </MDTypography>
    );
  };

  return (
    <DashboardLayout>
      <SbiHeader />
      <MDBox
        pt={3}
        pb={3}
        px={{ xs: 2, md: 3 }}
        sx={{
          backgroundColor: contentBg,
          color: contentColor,
          minHeight: "calc(100vh - 72px)",
          display: "flex",
          flexDirection: "column",
          "& .MuiTypography-root": {
            color: contentColor,
          },
        }}
      >
      {/* Main Content Area */}
      <MDBox
        width="100%"
        position="relative"
        p={{ xs: 2, md: 4 }}
      >
        <MDBox maxWidth="1200px" mx="auto">
          <Card
            sx={() => ({
              backgroundColor: cardBg,
              backdropFilter: "blur(12px)",
              border: `1px solid ${cardBorder}`,
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            })}
          >
            {/* Course Progress Runner */}
            <CourseProgressRunner
              currentStep={currentStep}
              totalSteps={totalSteps}
              title={courseConfig.title}
              subtitle={courseConfig.subtitle}
              onBack={handleBack}
            />

            {/* Content Area - Course specific content */}
            <MDBox
              p={3}
              sx={{ 
                minHeight: "400px",
              }}
            >
              {getQuestionForStep()}
            </MDBox>

            {/* Navigation Buttons */}
            <MDBox
              display="flex"
              gap={2}
              p={3}
              sx={() => ({
                borderTop: `1px solid ${cardBorder}`,
              })}
            >
              <MDButton
                variant="outlined"
                size="large"
                onClick={() => {
                  if (currentStep > 1) {
                    setCurrentStep(currentStep - 1);
                  } else {
                    handleBack();
                  }
                }}
                startIcon={<Icon>arrow_back</Icon>}
                sx={() => ({
                  flex: 1,
                  background: secondaryBtnBg,
                  border: `2px solid ${secondaryBtnBorder}`,
                  color: secondaryBtnColor,
                  fontWeight: "600",
                  "&:hover": {
                    background: secondaryBtnHoverBg,
                    borderColor: isFreseniusBranding ? FRESENIUS.blueLight : secondaryBtnBorder,
                  },
                })}
              >
                {currentStep === 1 ? "Back" : "Previous"}
              </MDButton>
              <MDButton
                variant="contained"
                size="large"
                onClick={() => {
                  if (currentStep < totalSteps) {
                    setCurrentStep(currentStep + 1);
                  }
                }}
                endIcon={currentStep === totalSteps ? <Icon>check</Icon> : <Icon>arrow_forward</Icon>}
                disabled={currentStep >= totalSteps}
                sx={() => ({
                  flex: 1,
                  background: currentStep >= totalSteps
                    ? `${primaryBtnDisabledBg} !important`
                    : `${primaryBtnBg} !important`,
                  color: `${primaryBtnColor} !important`,
                  fontWeight: "600",
                  border: "none !important",
                  boxShadow: "none !important",
                  "&:hover": {
                    background: currentStep >= totalSteps
                      ? `${primaryBtnDisabledBg} !important`
                      : `${primaryBtnHoverBg} !important`,
                    boxShadow: "none !important",
                  },
                  "&.Mui-disabled": {
                    background: `${primaryBtnDisabledBg} !important`,
                    color: `${primaryBtnColor} !important`,
                  },
                })}
              >
                {currentStep === totalSteps ? "Complete" : "Next"}
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default CoursePage;
